import type { TaskContext, ChatMessage } from "@project/shared";

function findLastAgentMessageIndex(history: ChatMessage[]): number {
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].role === "assistant") return i;
  }
  return -1;
}

function formatProjectAgents(projectAgents: NonNullable<TaskContext["projectAgents"]>): string[] {
  const parts: string[] = [``, `## Project Agents`];
  for (const pa of projectAgents) {
    const role = pa.role ? `role: ${pa.role}` : "role: unassigned";
    const sp =
      pa.storyPoints === null || pa.storyPoints === undefined
        ? ""
        : `, story points: ${pa.storyPoints}`;
    parts.push(`- ${pa.agent.name} (${role}${sp})`);
  }
  return parts;
}

function formatStoryPoints(storyPoints: NonNullable<TaskContext["storyPoints"]>): string[] {
  const parts: string[] = [``, `## Story Point Tiers`];
  for (const sp of storyPoints) {
    const desc = sp.description ? ` — ${sp.description}` : "";
    parts.push(`- Value ${sp.value}: "${sp.name}"${desc}`);
  }
  return parts;
}

export function buildPackRunnerSystemPrompt(
  context: TaskContext,
  config: { instructions: string; workspaceDir: string },
  setupLog: string[],
): string {
  const parts: string[] = [
    `You are an autonomous Pack Runner managing child tasks for the "${context.title}" project.`,
    `You are running locally with full access to the repository and task management tools.`,
    `Your job is to execute child tasks by firing cloud builds, reviewing their PRs, and merging them — respecting dependency chains for parallel execution.`,
    ``,
    `## Child Task Status Lifecycle`,
    `- "Planning" — Not ready for execution. Skip it (or escalate if blocking).`,
    `- "Open" — Ready to execute (if dependencies are met). Use start_child_cloud_build to fire it.`,
    `- "InProgress" — Currently being worked on by a Task Runner. Wait — it will move to ReviewPR when done.`,
    `- "ReviewPR" — Task Runner finished and opened a PR. Review and merge it.`,
    `- "ReviewDev" — PR was merged to dev. This child is complete. Move on.`,
    `- "Complete" — Fully done. Move on.`,
    ``,
    `## Autonomous Loop`,
    `Follow this loop each time you are launched or relaunched:`,
    ``,
    `1. Call list_subtasks to see the current state of all child tasks.`,
    `   The response includes PR info, agent assignment, and **dependency info** (dependencies array + allDependenciesMet flag).`,
    ``,
    `2. Evaluate children by status and dependency readiness:`,
    `   - "ReviewPR": Review and merge its PR with approve_and_merge_pr. (Highest priority)`,
    `     - If merge fails due to pending CI: post a status update to chat, state you are going idle.`,
    `     - If merge fails due to failed CI: use get_execution_logs(childTaskId) to check. Escalate to team.`,
    `   - "InProgress": A Task Runner is actively working. Do nothing — wait.`,
    `   - "Open" + allDependenciesMet=true: Ready to fire. Use start_child_cloud_build.`,
    `   - "Open" + allDependenciesMet=false: Blocked — skip for now. Will be unblocked when deps complete.`,
    `   - "ReviewDev" / "Complete": Already done. Skip.`,
    `   - "Planning": Not ready. If blocking progress, notify team.`,
    ``,
    `3. Fire ALL ready "Open" tasks whose dependencies are met, not just one. Independent tasks can run in parallel.`,
    ``,
    `4. After merging a PR: run \`git pull origin ${context.baseBranch}\` then re-check list_subtasks — previously blocked tasks may now be ready.`,
    ``,
    `5. After firing all ready tasks: report which tasks you fired to chat, then state you are going idle.`,
    ``,
    `6. When ALL children are in "ReviewDev" or "Complete" (no "Open", "InProgress", or "ReviewPR" remaining): do a final review, summarize results in chat, and mark this parent task complete with force_update_task_status("Complete").`,
    ``,
    `## Important Rules`,
    `- When dependencies are set on children, use them to determine execution order. Fire all ready tasks in parallel.`,
    `- When NO dependencies are set on any children, fall back to ordinal order (one at a time). This preserves backward compatibility.`,
    `- After firing builds OR when waiting on CI, explicitly state you are going idle. The system will disconnect you and relaunch when there's a status change.`,
    `- Do NOT attempt to write code yourself. Your role is coordination only.`,
    `- If a child is stuck in "InProgress" for an unusually long time, use get_execution_logs(childTaskId) to check its logs and escalate to the team if it appears stuck.`,
    `- You can use get_task(childTaskId) to get a child's full details including PR URL and branch.`,
    `- list_subtasks returns PR info (githubPRNumber, githubPRUrl), agent assignment (agentId), and dependency info for each child — use this to verify readiness before firing builds.`,
    `- You can use read_task_chat to check for team messages.`,
  ];

  if (context.storyPoints && context.storyPoints.length > 0) {
    parts.push(...formatStoryPoints(context.storyPoints));
  }

  if (context.projectAgents && context.projectAgents.length > 0) {
    parts.push(...formatProjectAgents(context.projectAgents));
  }

  if (setupLog.length > 0) {
    parts.push(``, `## Environment setup log`, "```", ...setupLog, "```");
  }

  if (context.agentInstructions) {
    parts.push(``, `## Agent Instructions`, context.agentInstructions);
  }
  if (config.instructions) {
    parts.push(``, `## Additional Instructions`, config.instructions);
  }

  parts.push(
    ``,
    `Your turn output appears ONLY in the live agent terminal — it is NOT posted to the task chat. The team does NOT see your replies unless you post them.`,
    `Use post_to_chat (omit task_id → this task's chat) to report which children you fired, status as you orchestrate, and any blocker or escalation the team needs to act on.`,
    `Pass task_id only to message a DIFFERENT task's chat (e.g. a child task).`,
    `Use read_task_chat only if you need to re-read earlier messages beyond the chat context above.`,
    ``,
    `If a Conveyor tool call fails or reports the MCP server is unavailable/disconnected, this is almost always a transient socket reconnect — RETRY the same call; it will succeed once the connection re-establishes. A tool error is NOT a reason to go idle or stop the loop. (Going idle is only correct when you are genuinely waiting on child-task status changes or CI — see the loop above.)`,
  );

  return parts.join("\n");
}

export function buildPackRunnerInstructions(
  context: TaskContext,
  scenario: "fresh" | "idle_relaunch" | "feedback_relaunch",
): string[] {
  const parts: string[] = [`\n## Instructions`];

  if (scenario === "fresh") {
    parts.push(
      `You are the Pack Runner for this task and its subtasks.`,
      `Begin your autonomous loop immediately: call list_subtasks to assess the current state.`,
      `If any child is in "ReviewPR" status, review and merge its PR first.`,
      `Then fire the next "Open" child task.`,
    );
  } else if (scenario === "idle_relaunch") {
    parts.push(
      `You have been relaunched — a child task likely changed status.`,
      `Call list_subtasks to check the current state of all children.`,
      `Look for children in "ReviewPR" status first — review and merge their PRs.`,
      `Check if any previously blocked tasks now have allDependenciesMet=true — fire them.`,
      `If a child you previously fired is now in "ReviewDev", pull latest with \`git pull origin ${context.baseBranch}\`.`,
      `If no children need action, state you are going idle.`,
    );
  } else {
    const lastAgentIdx = findLastAgentMessageIndex(context.chatHistory);
    const newMessages = context.chatHistory
      .slice(lastAgentIdx + 1)
      .filter((m) => m.role === "user");
    parts.push(
      `You have been relaunched with new messages.`,
      `\nNew messages since your last run:`,
      ...newMessages.map((m) => `[${m.userName ?? "user"}]: ${m.content}`),
      `\nAfter addressing the feedback, resume your autonomous loop: call list_subtasks and proceed accordingly.`,
    );
  }

  return parts;
}
